/* global window, setTimeout, clearTimeout */
import React, { useCallback, useEffect, useState } from "react";
import {
  Layout,
  Typography,
  Button,
  Space,
  Table,
  Input,
  Row,
  Col,
  Modal,
  Form,
  Select,
  DatePicker,
  message,
  Tag,
  Tooltip,
  Drawer,
  Timeline,
  Spin,
  Empty,
} from "antd";
import {
  PhoneOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  EditOutlined,
  ExportOutlined,
  SearchOutlined,
  SafetyOutlined,
  CloseCircleOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import moment from "moment";
import PhoneDisplay from "../common/PhoneDisplay";
import {
  getVonageNumbersListV3,
  createVonageNumberStatusRecordV3,
  purchaseTFNsFromVonageV3,
  exportVonageNumbersV3,
  getVonageNumberHistoryV3,
} from "../../reducers/nexmo/resources";
import config from "../../config";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS = [
  "Queued for Verification",
  "Submitted for Verification",
  "Verification in Review",
  "Verified",
  "Released",
];

const PURCHASE_OPTIONS = Array.from({ length: 10 }, (_, index) => ({
  label: `${index + 1}`,
  value: index + 1,
}));

const safeFormatDate = (value) => {
  if (!value || value === "Never") return "Never";
  const date = moment(value);
  return date.isValid() ? date.format("MM/DD/YYYY") : "-";
};

const safeFormatDateTime = (value) => {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("MMM D, YYYY") : "-";
};

const mapSortOrder = (order) => {
  if (order === "asc") return "ascend";
  if (order === "desc") return "descend";
  return null;
};

/** Colour + icon per status value */
const STATUS_META = {
  "Queued for Verification":   { color: "default",    icon: <ClockCircleOutlined /> },
  "Submitted for Verification":{ color: "processing",  icon: <ClockCircleOutlined /> },
  "Verification in Review":    { color: "warning",     icon: <ClockCircleOutlined /> },
  "Verified":                  { color: "success",     icon: <CheckCircleOutlined /> },
  "Released":                  { color: "error",       icon: <CloseCircleFilled   /> },
};

const statusMeta = (status) =>
  STATUS_META[status] || { color: "default", icon: <ClockCircleOutlined /> };

const VonageNumbers = () => {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Single consolidated query state — one object = one effect trigger, no stale closures
  const [queryState, setQueryState] = useState({
    page: 1,
    pageSize: 25,
    search: "",
    sortField: "created_at",
    sortOrder: "desc",
  });

  // Separate input state for debouncing
  const [searchInput, setSearchInput] = useState("");

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isSubmittingPurchase, setIsSubmittingPurchase] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  // History drawer state
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);   // the row that was clicked
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [purchaseForm] = Form.useForm();
  const [statusForm] = Form.useForm();

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  // Single effect that fires whenever any query param changes
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getVonageNumbersListV3({
          page: queryState.page,
          pageSize: queryState.pageSize,
          search: queryState.search,
          sort: queryState.sortField,
          order: queryState.sortOrder,
        });
        if (cancelled) return;
        const payload = response?.data || response;
        const data = payload?.data || payload || {};
        setNumbers(data.rows || []);
        setTotal(data.total || 0);
      } catch {
        if (!cancelled) message.error("Failed to load Vonage numbers.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [queryState]);

  // Manual refresh — just bump the queryState reference with same values to re-trigger
  const loadNumbers = useCallback(() => {
    setQueryState((prev) => ({ ...prev }));
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = searchInput.trim();
      setQueryState((prev) => {
        if (prev.search === nextSearch) return prev;
        clearSelection();
        return { ...prev, search: nextSearch, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, clearSelection]);

  // Open history drawer and fetch data
  const handleOpenHistory = useCallback(async (record) => {
    setHistoryRecord(record);
    setHistoryItems([]);
    setHistoryDrawerOpen(true);
    setHistoryLoading(true);
    try {
      const response = await getVonageNumberHistoryV3(record.id);
      const payload = response?.data || response;
      const data = payload?.data || payload || {};
      setHistoryItems(data.history || []);
    } catch {
      message.error("Failed to load status history.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleOpenPurchaseModal = () => {
    purchaseForm.setFieldsValue({ count: 1, notes: "" });
    setIsPurchaseModalOpen(true);
  };

  const handleOpenStatusModal = () => {
    if (!selectedRows.length) {
      message.info("Select at least one number to update status.");
      return;
    }

    statusForm.resetFields();
    setIsStatusModalOpen(true);
  };

  const handlePurchaseSubmit = async (values) => {
    setIsSubmittingPurchase(true);
    try {
      await purchaseTFNsFromVonageV3({
        count: Number(values.count),
        notes: values.notes.trim(),
      });

      message.success("Numbers successfully purchased.");
      setIsPurchaseModalOpen(false);
      purchaseForm.resetFields();
      await loadNumbers();
    } catch {
      message.error("Failed to purchase numbers.");
    } finally {
      setIsSubmittingPurchase(false);
    }
  };

  const handleStatusSubmit = async (values) => {
    setIsSubmittingStatus(true);
    try {
      await createVonageNumberStatusRecordV3({
        ids: selectedRows.map((row) => row.id),
        date: values.date.toDate().toISOString(),
        status: values.status,
        notes: values.notes.trim(),
      });

      message.success(`Status updated: ${values.status}`);
      setIsStatusModalOpen(false);
      statusForm.resetFields();
      clearSelection();
      await loadNumbers();
    } catch {
      message.error("Failed to update number status.");
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await exportVonageNumbersV3({
        search: queryState.search,
        sort: queryState.sortField,
        order: queryState.sortOrder,
      });
      const payload = response?.data || response || {};
      const fileName = payload.fileName;

      if (fileName) {
        window.location = `${config.api.host}/api/v1/files/contact/${fileName}`;
        message.success("Export started");
      } else {
        message.warning("No rows found to export");
      }
    } catch {
      message.error("Failed to export vonage numbers.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleTableChange = (tablePagination, _filters, sorter) => {
    const tableSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    // Detect if this onChange was triggered by a sort action (columnKey present)
    // vs. a pagination action (sorter is empty {}). Preserve sort during pagination.
    const isSortAction = tableSorter && tableSorter.columnKey;

    setQueryState((prev) => {
      if (!isSortAction) {
        // Pure pagination change — keep existing sort
        return {
          ...prev,
          page: tablePagination.current,
          pageSize: tablePagination.pageSize,
        };
      }

      // Sort action: resolve the new sort field/order
      // If order is undefined, user clicked 3rd time to remove sort → fall back to default
      const nextSortField = tableSorter.order ? (tableSorter.field || tableSorter.columnKey) : "created_at";
      const nextSortOrder = tableSorter.order === "ascend" ? "asc" : "desc";
      const sortChanged = prev.sortField !== nextSortField || prev.sortOrder !== nextSortOrder;

      return {
        ...prev,
        sortField: nextSortField,
        sortOrder: nextSortOrder,
        // When sort column/direction changes reset to page 1, else keep current page
        page: sortChanged ? 1 : tablePagination.current,
        pageSize: tablePagination.pageSize,
      };
    });
    clearSelection();
  };

  const columns = [
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      sorter: true,
      sortOrder: queryState.sortField === "mobile" ? mapSortOrder(queryState.sortOrder) : null,
      width: 170,
      render: (value) => value ? <PhoneDisplay value={value} /> : "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      sortOrder: queryState.sortField === "type" ? mapSortOrder(queryState.sortOrder) : null,
      width: 120,
      render: (value) => value || "-",
    },
    {
      title: "Latest Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: queryState.sortField === "status" ? mapSortOrder(queryState.sortOrder) : null,
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.status || "No status"}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.notes || "-"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status Date",
      dataIndex: "date",
      key: "date",
      sorter: true,
      sortOrder: queryState.sortField === "date" ? mapSortOrder(queryState.sortOrder) : null,
      width: 140,
      render: (value) => safeFormatDate(value),
    },
    {
      title: "Last Verified",
      dataIndex: "last_verified",
      key: "last_verified",
      sorter: true,
      sortOrder: queryState.sortField === "last_verified" ? mapSortOrder(queryState.sortOrder) : null,
      width: 140,
      render: (value) =>
        !value || value === "Never" ? <Tag>Never</Tag> : <Text>{safeFormatDate(value)}</Text>,
    },
    {
      title: "Current Company Assigned",
      dataIndex: "company",
      key: "company",
      sorter: true,
      sortOrder: queryState.sortField === "company" ? mapSortOrder(queryState.sortOrder) : null,
      render: (value) => value || "-",
    },
    {
      title: "Current Bot Assigned",
      dataIndex: "botname",
      key: "botname",
      sorter: true,
      sortOrder: queryState.sortField === "botname" ? mapSortOrder(queryState.sortOrder) : null,
      render: (value) => value || "-",
    },
    {
      title: "",
      key: "actions",
      width: 56,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View status history">
          <Button
            type="text"
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => handleOpenHistory(record)}
          />
        </Tooltip>
      ),
    },
  ];

  /* ── Timeline items built from history entries ── */
  const timelineItems = historyItems.map((entry) => {
    const meta = statusMeta(entry.status);
    return {
      key: entry.id,
      color: meta.color === "success" ? "green"
           : meta.color === "error"   ? "red"
           : meta.color === "warning" ? "orange"
           : meta.color === "processing" ? "blue"
           : "gray",
      dot: meta.icon,
      children: (
        <div style={{ paddingBottom: 4 }}>
          <Space align="center" style={{ marginBottom: 2 }}>
            <Tag color={meta.color} style={{ marginRight: 0 }}>
              {entry.status || "Unknown"}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {safeFormatDateTime(entry.date)}
            </Text>
          </Space>
          {entry.notes && (
            <div>
              <Text style={{ fontSize: 13 }}>{entry.notes}</Text>
            </div>
          )}
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Recorded {safeFormatDateTime(entry.created_at)}
            </Text>
          </div>
        </div>
      ),
    };
  });

  return (
    <Layout className="gh-compact-page">
      <Content className="gh-compact-panel" style={{ overflowY: "auto" }}>
        <div style={{ padding: "20px 20px 0", marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col xs={24} md={8}>
              <Title level={3} style={{ margin: 0, color: "#1f2937" }}>
                Vonage Numbers
              </Title>
            </Col>
            <Col xs={24} md={16} style={{ textAlign: "right" }}>
              <Space wrap>
                <Button icon={<ReloadOutlined />} onClick={loadNumbers} loading={loading}>
                  Refresh
                </Button>
                <Button icon={<ExportOutlined />} onClick={handleExport} loading={exportLoading}>
                  Export
                </Button>
                <Button icon={<EditOutlined />} onClick={handleOpenStatusModal} disabled={!selectedRows.length}>
                  Update Status
                </Button>
                <Button type="primary" icon={<ShoppingOutlined />} onClick={handleOpenPurchaseModal}>
                  Purchase TFN
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <div style={{ padding: "0 20px 20px" }}>
          <div className="gh-compact-toolbar" style={{ marginBottom: 8 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={10}>
                <Input
                  placeholder="Search by number, status, company, bot"
                  prefix={<SearchOutlined />}
                  allowClear
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  disabled={loading}
                />
              </Col>
              <Col xs={24} md={14} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
                <Tag color={selectedRows.length ? "processing" : "default"}>
                  {selectedRows.length} selected
                </Tag>
                {selectedRows.length > 0 && (
                  <Button type="text" icon={<CloseCircleOutlined />} onClick={clearSelection}>
                    Clear selection
                  </Button>
                )}
              </Col>
            </Row>
          </div>

          <div className="gh-compact-table-wrap" style={{ background: "#fff" }}>
            {queryState.search && (
              <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Text type="secondary">
                  Showing {total} matching number{total === 1 ? "" : "s"}.
                </Text>
              </div>
            )}
            <Table
              className="gh-themed-table"
              rowKey={(record, index) => `${record.id}-${index}`}
              columns={columns}
              dataSource={numbers}
              loading={loading}
              size="small"
              scroll={{ x: numbers.length > 0 ? 1200 : undefined }}
              onChange={handleTableChange}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys, rows) => {
                  setSelectedRowKeys(keys);
                  setSelectedRows(rows);
                },
              }}
              pagination={{
                current: queryState.page,
                pageSize: queryState.pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
              }}
            />
          </div>
        </div>

        {/* ── Purchase Modal ── */}
        <Modal
          title="Purchase New TFNs"
          open={isPurchaseModalOpen}
          onCancel={() => setIsPurchaseModalOpen(false)}
          onOk={() => purchaseForm.submit()}
          okText="Purchase"
          okButtonProps={{ loading: isSubmittingPurchase }}
          destroyOnClose
        >
          <Form
            layout="vertical"
            form={purchaseForm}
            initialValues={{ count: 1, notes: "" }}
            onFinish={handlePurchaseSubmit}
          >
            <Form.Item
              label="Quantity"
              name="count"
              rules={[{ required: true, message: "Please select a quantity." }]}
            >
              <Select options={PURCHASE_OPTIONS} />
            </Form.Item>

            <Form.Item
              label="Notes"
              name="notes"
              rules={[{ required: true, whitespace: true, message: "Please add notes." }]}
            >
              <TextArea rows={4} maxLength={500} showCount />
            </Form.Item>
          </Form>
        </Modal>

        {/* ── Update Status Modal ── */}
        <Modal
          title="Update Number Status"
          open={isStatusModalOpen}
          onCancel={() => setIsStatusModalOpen(false)}
          onOk={() => statusForm.submit()}
          okText="Update"
          okButtonProps={{ loading: isSubmittingStatus }}
          destroyOnClose
        >
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Text type="secondary">
              Updating:
              {" "}
              <Tooltip title={selectedRows.map((row) => row.mobile).join(", ")}>
                <Text>
                  {selectedRows.slice(0, 3).map((row) => row.mobile).join(", ")}
                  {selectedRows.length > 3 ? ` +${selectedRows.length - 3} more` : ""}
                </Text>
              </Tooltip>
            </Text>

            <Form layout="vertical" form={statusForm} onFinish={handleStatusSubmit}>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Please select a status." }]}
                  >
                    <Select
                      placeholder="Select status"
                      options={STATUS_OPTIONS.map((status) => ({
                        value: status,
                        label: status,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Date"
                    name="date"
                    rules={[{ required: true, message: "Please select a date." }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(current) => current && current.valueOf() > Date.now()}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="Notes"
                name="notes"
                rules={[{ required: true, whitespace: true, message: "Please add notes." }]}
              >
                <TextArea rows={4} maxLength={500} showCount />
              </Form.Item>
            </Form>
          </Space>
        </Modal>

        {/* ── Status History Drawer ── */}
        <Drawer
          title={
            <Space>
              <HistoryOutlined />
              <span>Status History</span>
              {historyRecord && (
                <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
                  — <PhoneDisplay value={historyRecord.mobile} />
                </Text>
              )}
            </Space>
          }
          placement="right"
          width={420}
          open={historyDrawerOpen}
          onClose={() => setHistoryDrawerOpen(false)}
          destroyOnClose
        >
          {historyLoading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 48 }}>
              <Spin size="large" />
            </div>
          ) : historyItems.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No status history recorded yet"
            />
          ) : (
            <Timeline
              mode="left"
              items={timelineItems}
              style={{ paddingTop: 8 }}
            />
          )}
        </Drawer>
      </Content>
    </Layout>
  );
};

export default VonageNumbers;
